import { api, tokenStorage, ApiClientError } from './apiClient';

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '';

export type SubtitleFormat = 'srt' | 'vtt';
export type SubtitleProviderName = 'opensubtitles' | 'gestdown';

/**
 * What we're finding subtitles FOR. TV must carry season/episode — the
 * backend refuses to search show-level ids (they collide with the movie
 * namespace and return subtitles for unrelated films).
 */
export type SubtitleTarget =
  | { type: 'movie'; tmdbId: number }
  | { type: 'tv'; tmdbId: number; season: number; episode: number };

export interface SubtitleListEntry {
  provider: SubtitleProviderName;
  fileId: string;
  language: string;
  releaseName: string | null;
  format: SubtitleFormat;
  notes: string | null;
  hearingImpaired: boolean;
  aiTranslated: boolean;
  downloadCount: number;
}

export interface SubtitleListResponse {
  target: SubtitleTarget;
  languages: string[];
  entries: SubtitleListEntry[];
}

/**
 * List subtitles for a movie or TV episode across the requested languages
 * (priority order — the backend appends 'en' as a fallback). Auth-gated, so
 * we go through the standard `api()` helper that attaches the bearer.
 */
export function listSubtitles(target: SubtitleTarget, langs: string[]): Promise<SubtitleListResponse> {
  const q = `?langs=${encodeURIComponent(langs.join(','))}`;
  const path = target.type === 'movie'
    ? `/api/subtitles/movie/${target.tmdbId}${q}`
    : `/api/subtitles/tv/${target.tmdbId}/${target.season}/${target.episode}${q}`;
  return api<SubtitleListResponse>(path, { auth: true });
}

/**
 * Fetches the subtitle file (always WebVTT server-side) with the auth bearer
 * and wraps it in a `blob:` URL ready to drop into a `<track src>`. Caller
 * owns the URL and MUST `URL.revokeObjectURL()` it when no longer needed
 * (the Watch page does this on selection change + unmount).
 *
 * We can't use the shared `api()` helper here because it expects JSON; the
 * file route streams text/vtt.
 */
export async function fetchSubtitleAsBlobUrl(provider: SubtitleProviderName, fileId: string): Promise<string> {
  const headers: Record<string, string> = {};
  const token = tokenStorage.get();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(
    `${API_BASE}/api/subtitles/file/${encodeURIComponent(provider)}/${encodeURIComponent(fileId)}`,
    { headers },
  );
  if (!res.ok) {
    const code = res.status === 429 ? 'SUBTITLE_QUOTA' : 'SUBTITLE_FILE';
    throw new ApiClientError(res.status, code, `Subtitle fetch failed (${res.status})`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
